'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Calendar, Users, TrendingUp, ArrowUpRight, Eye } from 'lucide-react';
import { Event, Group, BlogPost, MockUser } from '@/types';
import { getInitials, parseDateOnly } from '@/utils';
import { AttendeeAvatars } from '../events/AttendeeAvatars';
import { useLanguage } from '@/context/LanguageContext';
import { getStrapiImageUrl } from '@/lib/image-utils';

interface NewsSidebarProps {
  upcomingEvents: Event[];
  popularGroups: Group[];
  popularArticles: BlogPost[];
  users: MockUser[];
}

export function NewsSidebar({ upcomingEvents, popularGroups, popularArticles, users }: NewsSidebarProps) {
  const { t, language } = useLanguage();
  const dateLocale = language === 'es' ? 'es-ES' : 'en-US';

  return (
    <aside className="hidden lg:block w-80 flex-shrink-0">
      <div className="sticky top-4 space-y-6 max-h-[calc(100vh-2rem)] overflow-y-auto pr-2">

        {/* 1. Upcoming Events */}
        <section className="bg-background border border-border rounded-[14px] p-6">
          <div className="flex items-center gap-2 mb-6">
            <Calendar size={16} className="text-foreground" />
            <h2 className="text-xl font-semibold tracking-[-0.3125px] text-foreground">{t.sidebar.upcomingEvents}</h2>
          </div>

          <div className="space-y-3">
            {upcomingEvents.length === 0 ? (
              <p className="text-sm text-foreground-muted">{t.sidebar.noUpcomingEvents}</p>
            ) : (
              upcomingEvents.slice(0, 3).map((event) => {
                // parseDateOnly avoids UTC-midnight shifting YYYY-MM-DD into the previous day in MT.
                const eventDate = parseDateOnly(event.date);
                const monthShort = eventDate.toLocaleDateString(dateLocale, { month: 'short' });
                const day = eventDate.getDate();

                return (
                  <Link
                    key={event.documentId}
                    href={`/events/${event.documentId}`}
                    className="block hover:bg-sidebar-hover p-2 rounded-md transition focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-foreground mb-1">
                          {event.title}
                        </h3>
                        <AttendeeAvatars
                          attendees={event.attendees || []}
                          users={users}
                          maxVisible={3}
                          size="sm"
                        />
                      </div>
                      <div className="text-xs text-foreground-muted flex-shrink-0">
                        {monthShort} {day}
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          <Link
            href="/events"
            className="mt-4 w-full h-8 flex items-center justify-center text-sm font-medium text-black bg-action-green hover:opacity-90 rounded-lg transition focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {t.sidebar.viewAllEvents}
          </Link>
        </section>

        {/* 2. Getting Started */}
        <section className="bg-background border border-border rounded-[14px] p-6">
          <h2 className="text-xl font-semibold tracking-[-0.3125px] text-foreground mb-1">{t.sidebar.gettingStarted}</h2>
          <p className="text-sm text-foreground-muted mb-4">{t.sidebar.gettingStartedSubtitle}</p>

          <div className="space-y-3">
            {[
              { label: t.sidebar.communityGuidelines, href: '/guidelines-legal' },
              { label: t.sidebar.frequentQuestions, href: '/faqs' },
              { label: t.sidebar.aboutTogetherWe, href: '/about' },
            ].map(({ label, href }) => (
              <Link
                key={label}
                href={href}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition focus:outline-none focus:ring-2 focus:ring-primary rounded"
              >
                <ArrowUpRight size={16} className="flex-shrink-0 text-foreground-muted" />
                {label}
              </Link>
            ))}
          </div>
        </section>

        {/* 3. Popular Groups */}
        <section className="bg-background border border-border rounded-[14px] p-6">
          <div className="flex items-center gap-2 mb-6">
            <Users size={16} className="text-foreground" />
            <h2 className="text-xl font-semibold tracking-[-0.3125px] text-foreground">{t.sidebar.popularGroups}</h2>
          </div>

          <div className="space-y-3">
            {popularGroups.length === 0 ? (
              <p className="text-sm text-foreground-muted">{t.sidebar.noPopularGroups}</p>
            ) : (
              popularGroups.slice(0, 3).map((group) => {
                const avatarUrl = getStrapiImageUrl(group.avatar?.url);
                return (
                  <Link
                    key={group.documentId}
                    href={`/groups/${group.documentId}`}
                    className="flex items-center gap-3 hover:bg-sidebar-hover p-2 rounded-md transition focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <div
                      className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: '#00423E' }}
                    >
                      {avatarUrl ? (
                        <Image
                          src={avatarUrl}
                          alt={group.name}
                          width={40}
                          height={40}
                          className="w-full h-full object-cover"
                          unoptimized
                        />
                      ) : (
                        getInitials(group.name)
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground line-clamp-1">{group.name}</p>
                      <p className="text-xs text-foreground-muted">{group.memberCount ?? 0} {t.sidebar.members}</p>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          <Link
            href="/groups"
            className="mt-4 w-full h-8 flex items-center justify-center text-sm font-medium text-black bg-action-green hover:opacity-90 rounded-lg transition focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {t.sidebar.viewAllGroups}
          </Link>
        </section>

        {/* 4. Popular Articles */}
        <section className="bg-background border border-border rounded-[14px] p-6">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp size={16} className="text-foreground" />
            <h2 className="text-xl font-semibold tracking-[-0.3125px] text-foreground">{t.sidebar.popularArticles}</h2>
          </div>

          <div className="divide-y divide-border">
            {popularArticles.length === 0 ? (
              <p className="text-sm text-foreground-muted">{t.sidebar.noPopularArticles}</p>
            ) : (
              popularArticles.slice(0, 3).map((article) => (
                <Link
                  key={article.documentId}
                  href={`/blog/${article.documentId}`}
                  className="block py-3 first:pt-0 last:pb-0 hover:bg-sidebar-hover px-2 rounded-md transition focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <h3 className="text-sm font-medium text-foreground line-clamp-2 mb-1">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-foreground-muted">
                    <span>{article.readTime} {t.sidebar.minRead}</span>
                    <span className="flex items-center gap-1">
                      <Eye size={12} />
                      {article.views}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </section>

      </div>
    </aside>
  );
}

